import { BaseWebComponentController } from '../framework/BaseWebComponentController';
import { BaseWidgetWebComponent } from './BaseWidgetWebComponent';
import { BaseWidgetRepository } from './BaseWidgetRepository';
import { BaseWidgetView } from './BaseWidgetView';
import { GetWidgetDataRequest, BackendResponseWithMeta } from '../types/widget/types';
import { RequestOptions } from '@nirvana/shared/websocket-client';
import { Observable } from 'rxjs';

type DataFetchMethodType<T> = (request: GetWidgetDataRequest, options: RequestOptions) => Observable<T>;

interface IViewInitialSetup<T> {
  viewConstructor: typeof BaseWidgetView;
  viewTemplate: HTMLElement;
  dataFetchMethod: DataFetchMethodType<T>;
}

interface WidgetResultWithToken<T> {
  widgetResult: T;
  token: string;
}

export class BaseWidgetController<T> extends BaseWebComponentController {
  public view: BaseWidgetView;

  // Last successful result from the backend, or undefined if the last call to the backend failed.
  // IMPORTANT: After getting the result from the backend, the frontend (aka the widget) displays it.
  // Even this display might fail and result in an error, even if the call to the backend succeeded!
  // In this case however, this would be a valid result. Exporting a PDF with this result would result
  // in the same error that the user sees in the browser (since the webcomponent is shared).
  public lastLoadedResult?: any; // WidgetResultWithToken?

  constructor(component: BaseWidgetWebComponent) {
    super(component);
  }

  public initialWidgetSetup({ viewConstructor, viewTemplate, dataFetchMethod }: IViewInitialSetup<T>) {
    this.initialSetup({
      viewConstructor,
      viewTemplate,
      dataRepositoryConstructor: BaseWidgetRepository.bind(
        null,
        dataFetchMethod.bind(this.component.dependencies.api.clients.Widgets)
      ),
    });
  }

  async getDataForView(): Promise<BackendResponseWithMeta<T> | undefined> {
    const baseComponent: BaseWidgetWebComponent = this.component as BaseWidgetWebComponent;
    const baseDataRepository = this.dataRepository as BaseWidgetRepository<T>;
    const reportingPeriod = baseComponent.reportingPeriod;
    const targets = baseComponent.targets;

    if (reportingPeriod) {
      this.lastLoadedResult = undefined;

      this.view.showLoader();
      try {
        const response = await baseDataRepository.readValue({
          reportingPeriod: reportingPeriod,
          dashboardId: baseComponent.dashboardId,
          widgetId: baseComponent.widgetId,
          locale: this.dependencies.getUserLanguage(),
          timezone: this.dependencies.getTimezone(),
          targetIds: targets.map(target => target.companyId),
        });

        this.lastLoadedResult = response;

        return response
          ? {
              ...response,
              meta: {
                targets: targets,
                reportingPeriod: reportingPeriod,
                canDrillDown: baseComponent.canDrillDown,
                displayMode: baseComponent.displayMode,
              },
            }
          : undefined;
      } catch (error) {
        console.log(error);
        throw error;
      } finally {
        this.view.hideLoader();
        baseComponent.dispatchEvent(
          new CustomEvent('dataLoadingFinished', {
            bubbles: true,
          })
        );
      }
    } else {
      console.error(`Invalid reporting interval: ${JSON.stringify(reportingPeriod, null, 2)}`);
    }
  }
}
